import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api-fetch";
import { IS_LOCAL_DEMO } from "@/components/demo/DemoPackageSwitch";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Plus,
  Trash2,
  Package,
  Truck,
  ClipboardCheck,
  AlertTriangle,
  X,
  Search,
  ChevronDown,
  User,
  Paperclip,
  FileText,
  Image as ImageIcon,
  Building2,
  MapPin,
  Sparkles,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type QuantityType = "pallets" | "units" | "tons" | "yards";

type LineItem = {
  id: string;
  productName: string;
  productId: string; // product_id like "SSW-003"
  sizeCategory: string; // now stores the size category name
  sizeCategoryCode: string;
  quantity: number;
  quantityType: QuantityType;
};

type CachedProduct = {
  id: number;
  airtable_id?: string;
  product_name: string;
  product_id: string;
  generic_name?: string | null; // e.g. "dairy compost" for Simon's Gold
  ingredient_ratios?: string | null;
  ingredients_list?: string | null;
  size_categories: string[]; // airtable IDs
  certifications: string[] | null;
  illustration_url: string | null;
};

type SizeCategoryDB = {
  id: number;
  airtable_id: string;
  name: string;
  code: string;
  units_per_pallet: number | null;
  estimated_pallet_weight: number | null;
  illustration_url: string | null;
  pallet_configuration: string | null;
};

type OpsClient = {
  id: number;
  name: string;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  address: string | null;
};

type OrderType =
  | "Production"
  | "Amazon FBA"
  | "FBA labels"
  | "Local Sales"
  | "Wholesale"
  | "Bulk Blend";

type Priority = "low" | "normal" | "high" | "urgent";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ORDER_TYPES: OrderType[] = IS_LOCAL_DEMO ? ["Production", "Wholesale", "Bulk Blend"] : [
  "Production",
  "Amazon FBA",
  "FBA labels",
  "Local Sales",
  "Wholesale",
  "Bulk Blend",
];

const PRIORITIES: { value: Priority; label: string }[] = [
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

const STEPS = [
  { number: 1, label: "Products", icon: Package },
  { number: 2, label: "Transport", icon: Truck },
  { number: 3, label: "Review", icon: ClipboardCheck },
];

const INTERNAL_LOCATIONS = IS_LOCAL_DEMO ? [
  { label: "Demo Yard", address: "100 Demonstration Lane", city: "Congress", state: "AZ", zip: "85332" },
] : [
  { label: "Phoenix Office", address: "1634 N 19th Ave", city: "Phoenix", state: "AZ", zip: "85009" },
  { label: "Vicksburg Office", address: "Vicksburg", city: "Vicksburg", state: "AZ", zip: "" },
  { label: "Tucson Office", address: "Tucson", city: "Tucson", state: "AZ", zip: "" },
] as const;

// Fallback generic names for search (when ops_products_cache.generic_name is not set)
const GENERIC_NAME_ALIASES: Record<string, string> = {
  "Simon's Gold": "dairy compost",
};

function generateId() {
  return Math.random().toString(36).substring(2, 10);
}

function emptyLine(): LineItem {
  return {
    id: generateId(),
    productName: "",
    productId: "",
    sizeCategory: "",
    sizeCategoryCode: "",
    quantity: 1,
    quantityType: IS_LOCAL_DEMO ? "tons" : "pallets",
  };
}

// ---------------------------------------------------------------------------
// Product Combobox
// ---------------------------------------------------------------------------

function ProductCombobox({
  products,
  value,
  onChange,
}: {
  products: CachedProduct[];
  value: string;
  onChange: (product: CachedProduct | null, name: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = products.filter(
    (p) =>
      (p.product_name || "").toLowerCase().includes(query.toLowerCase()) ||
      (p.product_id || "").toLowerCase().includes(query.toLowerCase()) ||
      (p.generic_name || "").toLowerCase().includes(query.toLowerCase())
  );
  const customTitle = query.trim();
  const hasCustomTitle = customTitle.length > 0;
  const exactMatch = products.some(
    (p) => p.product_name.toLowerCase() === customTitle.toLowerCase()
  );

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <Search
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            // If they clear or type something different, clear the selection
            if (
              !products.some(
                (p) =>
                  p.product_name === e.target.value ||
                  (p.generic_name && p.generic_name === e.target.value)
              )
            ) {
              onChange(null, e.target.value);
            }
          }}
          onFocus={() => setOpen(true)}
          placeholder="Search catalog or type a special order title..."
          className="w-full pl-9 pr-8 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500"
        />
        <ChevronDown
          size={14}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
      </div>

      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-xl max-h-60 overflow-y-auto">
          {filtered.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => {
                setQuery(p.product_name);
                onChange(p, p.product_name);
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-2.5 flex items-center gap-3 hover:bg-secondary transition-colors text-sm ${
                value === p.product_name
                  ? "bg-forest-50 dark:bg-forest-950/20"
                  : ""
              }`}
            >
              <div className="flex-1 min-w-0">
                <span className="font-medium text-foreground block truncate">
                  {p.product_name}
                </span>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-muted-foreground font-mono">
                    {p.product_id}
                  </span>
                  {p.generic_name && (
                    <span className="text-xs text-muted-foreground/80">
                      · {p.generic_name}
                    </span>
                  )}
                </div>
              </div>
              {p.certifications && p.certifications.length > 0 && (
                <div className="flex gap-1 shrink-0">
                  {p.certifications.slice(0, 2).map((c) => (
                    <span
                      key={c}
                      className="px-1.5 py-0.5 text-[9px] font-bold rounded bg-forest-100 text-forest-700 dark:bg-forest-900/30 dark:text-forest-400"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              )}
              {value === p.product_name && (
                <Check
                  size={14}
                  className="text-forest-600 shrink-0"
                  strokeWidth={3}
                />
              )}
            </button>
          ))}
          {!exactMatch && (
            <button
              type="button"
              disabled={!hasCustomTitle}
              onClick={() => {
                if (!hasCustomTitle) return;
                setQuery(customTitle);
                onChange(null, customTitle);
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-3 flex items-center gap-3 transition-colors text-sm border-t border-border ${
                hasCustomTitle
                  ? "bg-earth-50 hover:bg-earth-100 dark:bg-earth-950/20 dark:hover:bg-earth-950/40 cursor-pointer"
                  : "bg-secondary/40 cursor-not-allowed opacity-70"
              }`}
            >
              <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                hasCustomTitle
                  ? "bg-earth-500 text-white"
                  : "bg-muted text-muted-foreground"
              }`}>
                <Sparkles size={14} strokeWidth={2.5} />
              </div>
              <div className="min-w-0 flex-1">
                {hasCustomTitle ? (
                  <>
                    <span className="font-semibold text-foreground block truncate">
                      Create special order: &ldquo;{customTitle}&rdquo;
                    </span>
                    <span className="text-xs text-earth-700 dark:text-earth-400">
                      This becomes the work order title
                    </span>
                  </>
                ) : (
                  <>
                    <span className="font-medium text-muted-foreground block">
                      Type a product name to create a special order
                    </span>
                    <span className="text-xs text-muted-foreground/70">
                      For non-catalog items
                    </span>
                  </>
                )}
              </div>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Size Category Grid
// ---------------------------------------------------------------------------

function SizeCategoryGrid({
  allSizes,
  selected,
  onChange,
}: {
  allSizes: SizeCategoryDB[];
  selected: string;
  onChange: (name: string, code: string) => void;
}) {
  // Show all size categories regardless of product — deduplicated, only with photos
  const [showCustom, setShowCustom] = useState(false);
  const [customValue, setCustomValue] = useState("");

  const available = (() => {
    // Deduplicate by code — DB should already be clean (one entry per size)
    const byCode = new Map<string, SizeCategoryDB>();
    for (const s of allSizes) {
      if (!IS_LOCAL_DEMO && !s.illustration_url) continue; // local samples can use a generic packaging icon
      if (!byCode.has(s.code)) {
        byCode.set(s.code, s);
      }
    }
    return Array.from(byCode.values());
  })();

  const isCustomSelected = selected !== "" && !available.some((s) => s.name === selected);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {available.map((s) => {
          const isSelected = selected === s.name;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => { onChange(s.name, s.code); setShowCustom(false); }}
              className={`relative flex flex-col items-center gap-1.5 p-2.5 rounded-xl border-2 transition-all text-center ${
                isSelected
                  ? "border-forest-500 bg-forest-50 dark:bg-forest-950/20 shadow-sm"
                  : "border-border hover:border-forest-300 dark:hover:border-forest-800 bg-background"
              }`}
            >
              {isSelected && (
                <div className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-forest-600 flex items-center justify-center">
                  <Check size={10} className="text-white" strokeWidth={3} />
                </div>
              )}
              {s.illustration_url ? <img
                src={s.illustration_url!}
                alt={s.name}
                className="w-14 h-14 rounded-lg object-contain bg-white dark:bg-secondary ring-1 ring-black/5"
              /> : <Package size={36} className="my-2.5 text-forest-500" />}
              <span
                className={`text-[11px] font-semibold leading-tight ${
                  isSelected
                    ? "text-forest-700 dark:text-forest-400"
                    : "text-foreground"
                }`}
              >
                {s.name}
              </span>
              {s.units_per_pallet && (
                <span className="text-[9px] text-muted-foreground">
                  {s.units_per_pallet}/pallet
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Custom size category */}
      {showCustom ? (
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={customValue}
            onChange={(e) => {
              setCustomValue(e.target.value);
              onChange(e.target.value, "other");
            }}
            placeholder="Enter custom size category..."
            className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500"
            autoFocus
          />
          <button
            type="button"
            onClick={() => { setShowCustom(false); if (!customValue.trim()) { onChange("", ""); } }}
            className="text-xs text-muted-foreground hover:text-foreground px-2 py-2"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowCustom(true)}
          className={`inline-flex items-center gap-1.5 text-xs font-medium transition-colors ${
            isCustomSelected
              ? "text-forest-700 dark:text-forest-400"
              : "text-muted-foreground hover:text-forest-600"
          }`}
        >
          <Package size={12} />
          {isCustomSelected ? `Custom: ${selected}` : "Custom size category"}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Client Combobox
// ---------------------------------------------------------------------------

function ClientCombobox({
  clients,
  value,
  onChange,
}: {
  clients: OpsClient[];
  value: string;
  onChange: (client: OpsClient | null, name: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = clients.filter(
    (c) =>
      (c.name || "").toLowerCase().includes(query.toLowerCase()) ||
      (c.contact_name || "").toLowerCase().includes(query.toLowerCase()) ||
      (c.contact_email || "").toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <User
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            if (!clients.some((c) => c.name === e.target.value)) {
              onChange(null, e.target.value);
            }
          }}
          onFocus={() => setOpen(true)}
          placeholder="Search clients or enter new name..."
          className="w-full pl-9 pr-8 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500"
        />
        <ChevronDown
          size={14}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
      </div>

      {open && filtered.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-xl max-h-60 overflow-y-auto">
          {filtered.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => {
                setQuery(c.name);
                onChange(c, c.name);
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-2.5 flex items-center gap-3 hover:bg-secondary transition-colors text-sm ${
                value === c.name
                  ? "bg-forest-50 dark:bg-forest-950/20"
                  : ""
              }`}
            >
              <div className="w-8 h-8 rounded-full bg-forest-100 dark:bg-forest-900/30 flex items-center justify-center shrink-0">
                <span className="text-[10px] font-bold text-forest-700 dark:text-forest-400">
                  {c.name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <span className="font-medium text-foreground block truncate">
                  {c.name}
                </span>
                <div className="flex items-center gap-2 flex-wrap">
                  {c.contact_name && (
                    <span className="text-xs text-muted-foreground">
                      {c.contact_name}
                    </span>
                  )}
                  {c.address && (
                    <span className="text-xs text-muted-foreground/70 truncate">
                      · {c.address}
                    </span>
                  )}
                </div>
              </div>
              {value === c.name && (
                <Check
                  size={14}
                  className="text-forest-600 shrink-0"
                  strokeWidth={3}
                />
              )}
            </button>
          ))}
        </div>
      )}

      {open && query.length > 0 && filtered.length === 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-xl p-4 text-center">
          <p className="text-sm text-muted-foreground">
            No clients match "{query}"
          </p>
          <p className="text-xs text-forest-600 dark:text-forest-400 mt-1">
            This client will be saved automatically when you create the work order
          </p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function CreateWorkOrder() {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1 - Products
  const [lines, setLines] = useState<LineItem[]>([emptyLine()]);
  const [cachedProducts, setCachedProducts] = useState<CachedProduct[]>([]);
  const [sizeCategories, setSizeCategories] = useState<SizeCategoryDB[]>([]);

  // Client
  const [clients, setClients] = useState<OpsClient[]>([]);
  const [selectedClient, setSelectedClient] = useState<OpsClient | null>(null);
  const [clientName, setClientName] = useState("");

  // Step 2 - Transport
  const [needsTransportation, setNeedsTransportation] = useState(false);
  const [internalLocation, setInternalLocation] = useState("");
  const [destination, setDestination] = useState({
    address: "",
    city: "",
    state: "",
    zip: "",
  });
  const [preferredDeliveryDate, setPreferredDeliveryDate] = useState("");
  const [preferredDeliveryTime, setPreferredDeliveryTime] = useState("");

  // Step 3 - Review
  const [orderType, setOrderType] = useState<OrderType>("Production");
  const [priority, setPriority] = useState<Priority>("normal");
  const [notes, setNotes] = useState("");
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  // -----------------------------------------------------------------------
  // Fetch product cache + size categories
  // -----------------------------------------------------------------------

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      apiFetch("/api/ops/work-orders/products", { headers }).then((r) =>
        r.ok ? r.json() : []
      ),
      apiFetch("/api/ops/work-orders/size-categories", { headers }).then((r) =>
        r.ok ? r.json() : []
      ),
      apiFetch("/api/ops/clients", { headers }).then((r) =>
        r.ok ? r.json() : []
      ),
    ])
      .then(([productsData, sizesData, clientsData]) => {
        // Products API returns array directly (not { products: [] })
        const raw = Array.isArray(productsData)
          ? productsData
          : productsData?.products || [];
        const products: CachedProduct[] = raw.map((p: CachedProduct) => ({
          ...p,
          generic_name: p.generic_name ?? GENERIC_NAME_ALIASES[p.product_name] ?? null,
        }));
        setCachedProducts(
          products.sort((a: CachedProduct, b: CachedProduct) =>
            a.product_name.localeCompare(b.product_name)
          )
        );
        setSizeCategories(Array.isArray(sizesData) ? sizesData : []);
        setClients(Array.isArray(clientsData) ? clientsData : []);
      })
      .catch(console.error);
  }, [token]);

  // -----------------------------------------------------------------------
  // Line item helpers
  // -----------------------------------------------------------------------

  const updateLine = (id: string, updates: Partial<LineItem>) => {
    setLines((prev) =>
      prev.map((line) => (line.id === id ? { ...line, ...updates } : line))
    );
  };

  const handleProductSelect = (
    lineId: string,
    product: CachedProduct | null,
    name: string
  ) => {
    const updates: Partial<LineItem> = {
      productName: name,
      productId: product?.product_id || "",
    };
    // If we switched products, reset size category
    const currentLine = lines.find((l) => l.id === lineId);
    if (currentLine && currentLine.productName !== name) {
      updates.sizeCategory = "";
      updates.sizeCategoryCode = "";
    }
    updateLine(lineId, updates);
  };

  const addLine = () => {
    setLines((prev) => [...prev, emptyLine()]);
  };

  const removeLine = (id: string) => {
    if (lines.length <= 1) return;
    setLines((prev) => prev.filter((line) => line.id !== id));
  };

  // -----------------------------------------------------------------------
  // Validation
  // -----------------------------------------------------------------------

  const step1Valid = lines.every(
    (line) => line.productName.trim() && line.quantity > 0
  );

  const step2Valid =
    !needsTransportation ||
    (clientName === "Internal"
      ? !!internalLocation
      : destination.address.trim() &&
        destination.city.trim() &&
        destination.state.trim());

  // Build validation error messages for user feedback
  const step1Errors: string[] = [];
  lines.forEach((line, idx) => {
    const label = lines.length > 1 ? `Line ${idx + 1}: ` : "";
    if (!line.productName.trim()) step1Errors.push(`${label}Select a catalog product or create a special order`);
    else if (line.quantity <= 0) step1Errors.push(`${label}Enter a quantity`);
  });

  const step2Errors: string[] = [];
  if (needsTransportation) {
    if (clientName === "Internal") {
      if (!internalLocation) step2Errors.push("Select an internal location");
    } else {
      if (!destination.address.trim()) step2Errors.push("Delivery address is required");
      if (!destination.city.trim()) step2Errors.push("City is required");
      if (!destination.state.trim()) step2Errors.push("State is required");
    }
  }

  // -----------------------------------------------------------------------
  // Submit
  // -----------------------------------------------------------------------

  const handleSubmit = async () => {
    if (!step1Valid || !step2Valid) return;

    setSubmitting(true);
    setError(null);

    try {
      // Helper: enrich a line item with full product + size category metadata
      const enrichLine = (line: LineItem) => {
        const product = cachedProducts.find((p) => p.product_name === line.productName);
        const sizeCat = sizeCategories.find(
          (sc) => sc.code === line.sizeCategoryCode || sc.name === line.sizeCategory
        );
        return {
          productName: line.productName,
          productId: product?.product_id || line.productId || null,
          airtableProductId: product?.airtable_id || null,
          ingredientRatios: product?.ingredient_ratios || null,
          ingredientsList: product?.ingredients_list || null,
          sizeCategory: line.sizeCategoryCode || sizeCat?.code || line.sizeCategory || "other",
          sizeCategoryName: sizeCat?.name || line.sizeCategory || null,
          unitsPerPallet: sizeCat?.units_per_pallet || null,
          estimatedPalletWeight: sizeCat?.estimated_pallet_weight || null,
          quantity: line.quantity,
          quantityType: line.quantityType,
        };
      };

      const firstLine = lines[0];
      const enrichedFirst = enrichLine(firstLine);
      const payload: Record<string, any> = {
        productName: enrichedFirst.productName,
        productId: enrichedFirst.productId,
        airtableProductId: enrichedFirst.airtableProductId,
        ingredientRatios: enrichedFirst.ingredientRatios,
        ingredientsList: enrichedFirst.ingredientsList,
        sizeCategory: enrichedFirst.sizeCategory,
        sizeCategoryName: enrichedFirst.sizeCategoryName,
        unitsPerPallet: enrichedFirst.unitsPerPallet,
        estimatedPalletWeight: enrichedFirst.estimatedPalletWeight,
        quantity: enrichedFirst.quantity,
        quantityType: enrichedFirst.quantityType,
        lines: lines.map(enrichLine),
        needsTransportation,
        priority,
        orderType,
        workOrderNotes: notes.trim() || null,
        clientId: selectedClient?.id || null,
        clientName: clientName.trim() || null,
        internalLocation: clientName === "Internal" ? (internalLocation || null) : null,
      };

      if (needsTransportation) {
        payload.destinationAddress = destination.address.trim();
        payload.destinationCity = destination.city.trim();
        payload.destinationState = destination.state.trim();
        payload.destinationZip = destination.zip.trim();
        if (preferredDeliveryDate)
          payload.preferredDeliveryDate = preferredDeliveryDate;
        if (preferredDeliveryTime)
          payload.preferredDeliveryTime = preferredDeliveryTime;
      }

      const res = await apiFetch("/api/ops/work-orders", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          data.error || `Failed to create work order (${res.status})`
        );
      }

      const createdWo = await res.json().catch(() => null);
      const woId = createdWo?.id;

      // Upload pending files if any
      if (woId && pendingFiles.length > 0) {
        for (const file of pendingFiles) {
          const formData = new FormData();
          formData.append("file", file);
          try {
            await apiFetch(`/api/ops/work-orders/${woId}/attachments`, {
              method: "POST",
              headers: { Authorization: `Bearer ${token}` },
              body: formData,
            });
          } catch {
            // Non-blocking: files can be uploaded later from the detail page
          }
        }
      }

      navigate("/app/ops/work-orders");
    } catch (err: any) {
      setError(err?.message || "Failed to create work order");
    } finally {
      setSubmitting(false);
    }
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div className="space-y-8 max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/app/ops/work-orders")}
          className="p-2 text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg transition-colors"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="heading-display text-3xl text-foreground">
            Create Work Order
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Step {step} of 3 - {STEPS[step - 1].label}
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="flex items-center gap-2">
        {STEPS.map((s) => {
          const Icon = s.icon;
          const isActive = step === s.number;
          const isComplete = step > s.number;

          return (
            <div key={s.number} className="flex items-center gap-2 flex-1">
              <div
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-colors flex-1 ${
                  isActive
                    ? "bg-forest-600 text-white"
                    : isComplete
                    ? "bg-forest-100 text-forest-700 dark:bg-forest-900/30 dark:text-forest-400"
                    : "bg-secondary text-muted-foreground"
                }`}
              >
                {isComplete ? (
                  <Check size={14} strokeWidth={3} />
                ) : (
                  <Icon size={14} />
                )}
                <span className="hidden sm:inline">{s.label}</span>
                <span className="sm:hidden">{s.number}</span>
              </div>
              {s.number < 3 && (
                <div
                  className={`hidden sm:block h-px flex-grow max-w-8 ${
                    step > s.number ? "bg-forest-400" : "bg-border"
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900 rounded-xl p-4 flex items-center gap-3">
          <AlertTriangle
            size={16}
            className="text-red-600 dark:text-red-400 shrink-0"
          />
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-400 hover:text-red-600"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Step 1: Products */}
      {step === 1 && (
        <div className="bg-card border border-border rounded-xl p-6 space-y-6">
          <div>
            <h2 className="text-lg font-bold text-foreground">Line Items</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Add one or more products. Mix and match catalog items with special orders on the same work order.
            </p>
          </div>

          <div className="space-y-4">
            {lines.map((line, index) => {
              const isSpecialOrder = line.productName.trim().length > 0 && !line.productId;
              return (
              <div
                key={line.id}
                className="border border-border rounded-xl p-4 space-y-4 bg-background"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Line {index + 1}
                    </span>
                    {isSpecialOrder && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-full bg-earth-100 text-earth-800 dark:bg-earth-900/40 dark:text-earth-300">
                        <Sparkles size={10} />
                        Special Order
                      </span>
                    )}
                  </div>
                  {lines.length > 1 && (
                    <button
                      onClick={() => removeLine(line.id)}
                      className="p-1 text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>

                {/* Product search */}
                <div>
                  <label className="label-caps text-muted-foreground mb-2 block">
                    Product *
                  </label>
                  <ProductCombobox
                    products={cachedProducts}
                    value={line.productName}
                    onChange={(product, name) =>
                      handleProductSelect(line.id, product, name)
                    }
                  />
                  {line.productId && (
                    <p className="text-[11px] text-forest-600 dark:text-forest-400 font-mono mt-1">
                      {line.productId}
                    </p>
                  )}
                </div>

                {/* Size Category — visual grid */}
                <div>
                  <label className="label-caps text-muted-foreground mb-2 block">
                    Size / Packaging
                  </label>
                  <SizeCategoryGrid
                    allSizes={sizeCategories}
                    selected={line.sizeCategory}
                    onChange={(name, code) =>
                      updateLine(line.id, {
                        sizeCategory: name,
                        sizeCategoryCode: code,
                      })
                    }
                  />
                </div>

                {/* Quantity */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label-caps text-muted-foreground mb-2 block">
                      Quantity *
                    </label>
                    <input
                      type="number"
                      min={IS_LOCAL_DEMO ? "0.01" : "1"}
                      step={IS_LOCAL_DEMO ? "0.01" : "1"}
                      value={line.quantity}
                      onChange={(e) =>
                        updateLine(line.id, {
                          quantity: IS_LOCAL_DEMO ? Math.max(0, parseFloat(e.target.value) || 0) : Math.max(
                            1,
                            parseInt(e.target.value) || 1
                          ),
                        })
                      }
                      className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500"
                    />
                  </div>
                  <div>
                    <label className="label-caps text-muted-foreground mb-2 block">
                      Unit Type
                    </label>
                    <select
                      value={line.quantityType}
                      onChange={(e) =>
                        updateLine(line.id, {
                          quantityType: e.target.value as QuantityType,
                        })
                      }
                      className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500"
                    >
                      <option value="pallets">Pallets</option>
                      <option value="units">Units</option>
                      {IS_LOCAL_DEMO && <option value="tons">US short tons</option>}
                      {IS_LOCAL_DEMO && <option value="yards">Cubic yards</option>}
                    </select>
                  </div>
                </div>
              </div>
              );
            })}
          </div>

          <button
            onClick={addLine}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-forest-700 dark:text-forest-400 hover:bg-forest-50 dark:hover:bg-forest-950/20 border border-dashed border-forest-300 dark:border-forest-800 rounded-lg transition-colors"
          >
            <Plus size={14} />
            Add Another Line
          </button>
        </div>
      )}

      {/* Step 2: Client & Transport */}
      {step === 2 && (
        <div className="space-y-6">
          {/* Client Selection */}
          <div className="bg-card border border-border rounded-xl p-6 space-y-4">
            <div>
              <h2 className="text-lg font-bold text-foreground">Client</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Select an existing client or enter a new one
              </p>
            </div>

            {/* Quick-select: Internal */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  if (clientName === "Internal") {
                    setClientName("");
                    setSelectedClient(null);
                    setInternalLocation("");
                  } else {
                    setClientName("Internal");
                    setSelectedClient(null);
                    setInternalLocation("");
                  }
                }}
                className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                  clientName === "Internal"
                    ? "bg-forest-600 text-white border-forest-600"
                    : "bg-background text-muted-foreground border-border hover:bg-secondary hover:text-foreground"
                }`}
              >
                <Building2 size={14} />
                Internal
              </button>
              <span className="text-xs text-muted-foreground">or search below</span>
            </div>

            {clientName !== "Internal" && (
              <ClientCombobox
                clients={clients}
                value={clientName}
                onChange={(client, name) => {
                  setSelectedClient(client);
                  setClientName(name);
                  setInternalLocation("");
                  // Auto-fill address from client if available
                  if (client?.address) {
                    // Parse "street, city, state zip" format
                    const parts = client.address.split(",").map((s) => s.trim());
                    if (parts.length >= 2) {
                      const street = parts[0];
                      const cityStateZip = parts.slice(1).join(", ").trim();
                      // Try to parse "City, ST ZIP" or "City ST ZIP"
                      const match = cityStateZip.match(/^(.+?),?\s+([A-Z]{2})\s+(\d{5}(?:-\d{4})?)$/i);
                      if (match) {
                        setDestination({
                          address: street,
                          city: match[1].trim(),
                          state: match[2].toUpperCase(),
                          zip: match[3],
                        });
                      } else {
                        setDestination({
                          address: client.address,
                          city: "",
                          state: "",
                          zip: "",
                        });
                      }
                      setNeedsTransportation(true);
                    }
                  }
                }}
              />
            )}

            {clientName === "Internal" && (
              <div className="bg-forest-50 dark:bg-forest-950/20 border border-forest-200 dark:border-forest-800 rounded-lg p-3">
                <p className="text-sm text-forest-700 dark:text-forest-400 font-medium flex items-center gap-2">
                  <Building2 size={14} />
                  Internal work order — no external client
                </p>
              </div>
            )}

            {selectedClient && clientName !== "Internal" && (
              <div className="flex items-center gap-4 text-xs text-muted-foreground bg-secondary/50 rounded-lg p-3">
                {selectedClient.contact_name && (
                  <span>Contact: {selectedClient.contact_name}</span>
                )}
                {selectedClient.contact_email && (
                  <span>{selectedClient.contact_email}</span>
                )}
                {selectedClient.contact_phone && (
                  <span>{selectedClient.contact_phone}</span>
                )}
              </div>
            )}
          </div>

          {/* Transportation */}
          <div className="bg-card border border-border rounded-xl p-6 space-y-6">
          <div>
            <h2 className="text-lg font-bold text-foreground">
              Transportation
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Does this order require delivery?
            </p>
          </div>

          {/* Toggle */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setNeedsTransportation(!needsTransportation)}
              className={`relative w-11 h-6 rounded-full transition-colors ${
                needsTransportation
                  ? "bg-forest-600"
                  : "bg-gray-300 dark:bg-gray-700"
              }`}
            >
              <div
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  needsTransportation ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
            <span className="text-sm font-medium text-foreground">
              Needs Transportation
            </span>
          </div>

          {needsTransportation && (
            <div className="space-y-4 border-t border-border pt-4">
              {/* Internal Location Picker */}
              {clientName === "Internal" && (
                <div>
                  <label className="label-caps text-muted-foreground mb-2 block">
                    Internal Location *
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {INTERNAL_LOCATIONS.map((loc) => (
                      <button
                        key={loc.label}
                        type="button"
                        onClick={() => {
                          setInternalLocation(loc.label);
                          setDestination({
                            address: loc.address,
                            city: loc.city,
                            state: loc.state,
                            zip: loc.zip,
                          });
                        }}
                        className={`flex items-center gap-2 px-3 py-3 rounded-lg border text-sm font-medium transition-colors ${
                          internalLocation === loc.label
                            ? "bg-forest-600 text-white border-forest-600"
                            : "bg-background text-foreground border-border hover:bg-secondary"
                        }`}
                      >
                        <MapPin size={14} />
                        {loc.label}
                      </button>
                    ))}
                  </div>
                  {internalLocation && (
                    <p className="text-xs text-muted-foreground mt-2">
                      {destination.address}, {destination.city}, {destination.state} {destination.zip}
                    </p>
                  )}
                </div>
              )}

              {/* Manual address fields (shown for external clients or when no internal location flow) */}
              {clientName !== "Internal" && (
                <>
              <div>
                <label className="label-caps text-muted-foreground mb-2 block">
                  Destination Address *
                </label>
                <input
                  type="text"
                  value={destination.address}
                  onChange={(e) =>
                    setDestination({ ...destination, address: e.target.value })
                  }
                  placeholder="Street address"
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="label-caps text-muted-foreground mb-2 block">
                    City *
                  </label>
                  <input
                    type="text"
                    value={destination.city}
                    onChange={(e) =>
                      setDestination({ ...destination, city: e.target.value })
                    }
                    placeholder="City"
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500"
                  />
                </div>
                <div>
                  <label className="label-caps text-muted-foreground mb-2 block">
                    State *
                  </label>
                  <input
                    type="text"
                    value={destination.state}
                    onChange={(e) =>
                      setDestination({ ...destination, state: e.target.value })
                    }
                    placeholder="AZ"
                    maxLength={2}
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500 uppercase"
                  />
                </div>
                <div>
                  <label className="label-caps text-muted-foreground mb-2 block">
                    ZIP
                  </label>
                  <input
                    type="text"
                    value={destination.zip}
                    onChange={(e) =>
                      setDestination({ ...destination, zip: e.target.value })
                    }
                    placeholder="85009"
                    maxLength={10}
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500"
                  />
                </div>
              </div>
                </>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="label-caps text-muted-foreground mb-2 block">
                    Preferred Delivery Date
                  </label>
                  <input
                    type="date"
                    value={preferredDeliveryDate}
                    onChange={(e) => setPreferredDeliveryDate(e.target.value)}
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500"
                  />
                </div>
                <div>
                  <label className="label-caps text-muted-foreground mb-2 block">
                    Preferred Time
                  </label>
                  <input
                    type="time"
                    value={preferredDeliveryTime}
                    onChange={(e) => setPreferredDeliveryTime(e.target.value)}
                    className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        </div>
      )}

      {/* Step 3: Review */}
      {step === 3 && (
        <div className="space-y-6">
          {/* Order settings */}
          <div className="bg-card border border-border rounded-xl p-6 space-y-4">
            <h2 className="text-lg font-bold text-foreground">
              Order Details
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label-caps text-muted-foreground mb-2 block">
                  Order Type
                </label>
                <select
                  value={orderType}
                  onChange={(e) => setOrderType(e.target.value as OrderType)}
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500"
                >
                  {ORDER_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label-caps text-muted-foreground mb-2 block">
                  Priority
                </label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value as Priority)}
                  className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500"
                >
                  {PRIORITIES.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="label-caps text-muted-foreground mb-2 block">
                Notes
              </label>
              <textarea
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notes about this work order..."
                className="w-full px-3 py-2.5 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-forest-500/50 focus:border-forest-500 resize-none"
              />
            </div>

            {/* File Attachments */}
            {!IS_LOCAL_DEMO && <div>
              <label className="label-caps text-muted-foreground mb-2 block">
                <Paperclip size={12} className="inline mr-1" />
                Attachments
              </label>
              <label className="flex items-center justify-center gap-2 w-full px-4 py-3 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-forest-500 hover:bg-forest-50 dark:hover:bg-forest-900/10 transition-colors">
                <Paperclip size={16} className="text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Click to attach files (PDF, images, documents)
                </span>
                <input
                  type="file"
                  multiple
                  onChange={(e) => {
                    if (e.target.files) {
                      setPendingFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
                    }
                    e.target.value = "";
                  }}
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt"
                />
              </label>
              {pendingFiles.length > 0 && (
                <div className="mt-2 space-y-1.5">
                  {pendingFiles.map((f, i) => (
                    <div
                      key={`${f.name}-${i}`}
                      className="flex items-center gap-2.5 px-3 py-2 bg-secondary/40 rounded-lg"
                    >
                      {f.type.startsWith("image/") ? (
                        <ImageIcon size={14} className="text-blue-500 shrink-0" />
                      ) : (
                        <FileText size={14} className="text-orange-500 shrink-0" />
                      )}
                      <span className="text-sm text-foreground truncate flex-1">{f.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {(f.size / 1024).toFixed(0)} KB
                      </span>
                      <button
                        type="button"
                        onClick={() => setPendingFiles((prev) => prev.filter((_, idx) => idx !== i))}
                        className="p-1 text-muted-foreground hover:text-red-500"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>}
          </div>

          {/* Summary */}
          <div className="bg-card border border-border rounded-xl p-6 space-y-4">
            <h2 className="text-lg font-bold text-foreground">
              Order Summary
            </h2>

            {/* Client */}
            {clientName && (
              <div className="flex items-center gap-2 text-sm">
                <User size={14} className="text-forest-600" />
                <span className="text-muted-foreground">Client:</span>
                <span className="font-medium text-foreground">{clientName}</span>
                {!selectedClient && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 font-semibold">
                    NEW
                  </span>
                )}
              </div>
            )}

            {/* Line items */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2.5">#</th>
                    <th className="px-3 py-2.5">Product</th>
                    <th className="px-3 py-2.5">Size</th>
                    <th className="px-3 py-2.5 text-right">Qty</th>
                    <th className="px-3 py-2.5">Type</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, i) => (
                    <tr
                      key={line.id}
                      className="border-b border-border/60 last:border-0"
                    >
                      <td className="px-3 py-2.5 text-muted-foreground">
                        {i + 1}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="font-medium text-foreground">
                          {line.productName}
                        </span>
                        {line.productId && (
                          <span className="text-xs text-muted-foreground font-mono ml-2">
                            {line.productId}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground">
                        {line.sizeCategory || "—"}
                      </td>
                      <td className="px-3 py-2.5 text-right text-foreground tabular-nums">
                        {line.quantity}
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground capitalize">
                        {line.quantityType}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Transport info */}
            {needsTransportation && (
              <div className="border-t border-border pt-4">
                <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                  <Truck size={14} className="text-forest-600" />
                  Transportation
                </h3>
                <div className="text-sm text-muted-foreground space-y-1">
                  {clientName === "Internal" && internalLocation ? (
                    <p className="flex items-center gap-1.5">
                      <MapPin size={13} />
                      {internalLocation}
                    </p>
                  ) : (
                  <p>
                    {destination.address}, {destination.city},{" "}
                    {destination.state} {destination.zip}
                  </p>
                  )}
                  {preferredDeliveryDate && (
                    <p>
                      Delivery:{" "}
                      {new Date(
                        preferredDeliveryDate + "T00:00:00"
                      ).toLocaleDateString("en-US", {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                      {preferredDeliveryTime &&
                        ` at ${preferredDeliveryTime}`}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Meta */}
            <div className="border-t border-border pt-4 grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Order Type:</span>{" "}
                <span className="font-medium text-foreground">
                  {orderType}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Priority:</span>{" "}
                <span className="font-medium text-foreground capitalize">
                  {priority}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation buttons */}
      <div className="flex items-center justify-between pt-2">
        <button
          onClick={() => {
            if (step === 1) navigate("/app/ops/work-orders");
            else setStep(step - 1);
          }}
          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-secondary"
        >
          <ArrowLeft size={16} />
          {step === 1 ? "Cancel" : "Back"}
        </button>

        <div className="flex items-center gap-3">
          {/* Validation hints */}
          {step === 1 && !step1Valid && step1Errors.length > 0 && (
            <span className="text-xs text-amber-600 dark:text-amber-400">{step1Errors[0]}</span>
          )}
          {step === 2 && !step2Valid && step2Errors.length > 0 && (
            <span className="text-xs text-amber-600 dark:text-amber-400">{step2Errors[0]}</span>
          )}
          {step === 3 && (!step1Valid || !step2Valid) && (
            <span className="text-xs text-amber-600 dark:text-amber-400">
              {!step1Valid ? "Fix product details (Step 1)" : "Fix delivery details (Step 2)"}
            </span>
          )}

          {step < 3 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={(step === 1 && !step1Valid) || (step === 2 && !step2Valid)}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-forest-600 hover:bg-forest-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-colors shadow-sm shadow-forest-600/20"
            >
              Next
              <ArrowRight size={16} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting || !step1Valid || !step2Valid}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-forest-600 hover:bg-forest-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-colors shadow-sm shadow-forest-600/20"
            >
              {submitting ? (
                "Creating..."
              ) : (
                <>
                  <Check size={16} strokeWidth={2.5} />
                  Create Work Order
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
